import type { EstimateDetail, MappedTuple, PageMetadata } from "@/interfaces/estimate-detail";
import type { MeasurementData } from "@/interfaces/estimation.interface";
import { Fragment } from "react";
import type { Category } from "./Lines";
import { Room } from "./Room";
import { SelectionToolbar } from "./SelectionToolbar/SelectionToolbar";

interface RoomsProps {
  estimateId?: string;
  rooms: Array<EstimateDetail>;
  onItemLineDelete: (itemId: string, pageId: string, uniqueId: string, category?: Category) => void;
  onSelectItemLine: (uniqueId: string, category?: Category) => void;
  updateMeasurement: (measurementData: MeasurementData[]) => void;
  updateMetadataForExpectedDoc: (metadataUpdates: Partial<PageMetadata>) => void;
  updateLineItems: (pageId: string, lineItems: MappedTuple[]) => Promise<boolean>;
  getRoomRef?: (roomName: string) => (el: HTMLDivElement | null) => void; // New prop
}

export const Rooms = ({
  rooms,
  onItemLineDelete,
  onSelectItemLine,
  updateMeasurement,
  updateMetadataForExpectedDoc,
  updateLineItems,
  getRoomRef,
  estimateId,
}: RoomsProps) => {
  // Unmatched photo clusters (from scans that couldn't be matched to a documented room) are no
  // longer surfaced in the estimate view — only render rooms that matched the documentation.
  const matchedRooms = rooms.filter((room) => !room.isUnmatchedCluster);

  return (
    <div className="mb-8">
      <Fragment>
        {matchedRooms.map((room, idx) => (
          <RoomsListItem
            key={`room_${idx}`}
            onItemLineDelete={onItemLineDelete}
            onSelectItemLine={onSelectItemLine}
            updateMeasurement={updateMeasurement}
            updateMetadataForExpectedDoc={updateMetadataForExpectedDoc}
            updateLineItems={updateLineItems}
            getRoomRef={getRoomRef}
            estimateId={estimateId!}
            room={room}
          />
        ))}
      </Fragment>
      <SelectionToolbar estimateId={estimateId} />
    </div>
  );
};

const RoomsListItem = ({
  onItemLineDelete,
  onSelectItemLine,
  updateMeasurement,
  updateMetadataForExpectedDoc,
  updateLineItems,
  getRoomRef,
  estimateId,
  room,
}: Omit<RoomsProps, "rooms"> & { room: EstimateDetail }) => (
  <div key={`${room.dataForUI.metadata.pageId}`} ref={getRoomRef?.(room.dataForUI.metadata.pageId)}>
    <Room
      estimateId={estimateId!}
      room={room}
      onItemLineDelete={onItemLineDelete}
      onSelectItemLine={onSelectItemLine}
      updateMeasurement={updateMeasurement}
      updateMetadataForExpectedDoc={updateMetadataForExpectedDoc}
      updateLineItems={updateLineItems}
    />
  </div>
);
